import type { Order, WindowBucket } from "~/features/orders/types-order";
import { formatWindowFilterValue, formatWindowRange, type WindowLike } from "~/shared/utils/delivery-window";

/** Default locale for all formatting functions */
const LOCALE = "en-US";

/** Fallback for empty/undefined values (legacy single dash) */
const EMPTY_VALUE = "—";

/**
 * Display "--" for empty, null, undefined, or invalid values. Use in tables/lists across the app.
 */
export function orDoubleDash(value: string | number | null | undefined): string {
    if (value == null) return "--";
    const s = String(value).trim();
    if (s === "" || s === "—" || s === "Invalid Date") return "--";
    return s;
}

/**
 * Formats a date string to short format (e.g., "Jan 23")
 * Uses UTC to prevent timezone-related date shifts (e.g., Feb 23 showing as Feb 22 in UTC-5)
 * @param date - ISO date string (YYYY-MM-DD or full ISO)
 */
export function formatDate(date: string): string {
    return new Date(date).toLocaleDateString(LOCALE, { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * Formats a date string to include year (e.g., "Jan 23, 2026")
 * @param date - ISO date string (YYYY-MM-DD or full ISO)
 */
export function formatDateWithYear(date: string): string {
    return new Date(date).toLocaleDateString(LOCALE, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * Extracts and formats the delivery window from an order
 * @param order - Order object with window preferences
 */
export function formatWindow(order: Order): string {
    const win = order.last_window || order.preferred_window;
    if (!win) return "No window";
    return `${win.start}:00-${win.end}:00`;
}

/**
 * Formats a WindowBucket to time range string
 * @param window - WindowBucket with start/end hours
 */
export function formatWindowBucket(window?: WindowBucket | null): string {
    return window ? `${window.start}:00-${window.end}:00` : EMPTY_VALUE;
}

/**
 * Formats a window as an AM/PM time range (e.g. "9:00 AM - 1:00 PM", "9:30 AM - 2:30 PM").
 * Uses exact minutes when present, otherwise the start/end hours.
 */
export function formatWindowRangeAMPM(window: WindowLike): string {
    return formatWindowRange(window);
}

/** Window as an AM/PM time range ("9:00 AM - 1:00 PM"); Morning/Midday/Evening names are never shown. */
export function formatWindowLabel(window?: WindowLike | null): string {
    return formatWindowRange(window);
}

/**
 * Formats a Unix timestamp (seconds) to time string (e.g., "2:30 PM")
 * @param timestamp - Unix timestamp in seconds
 */
export function formatTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleTimeString(LOCALE, {
        hour: "numeric",
        minute: "2-digit"
    });
}

/**
 * Formats a Unix timestamp (seconds) to both date and time strings
 * @param timestamp - Unix timestamp in seconds
 */
export function formatDateTime(timestamp: number): { date: string; time: string } {
    const d = new Date(timestamp * 1000);
    return {
        date: d.toLocaleDateString(LOCALE, { month: "short", day: "numeric" }),
        time: d.toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit" }),
    };
}

/**
 * Formats an ISO date string to both date and time strings
 * @param isoString - ISO date string (e.g., "2026-02-03T13:07:15.821Z")
 */
export function formatDateTimeFromISO(isoString: string): { date: string; time: string } {
    const d = new Date(isoString);
    return {
        date: d.toLocaleDateString(LOCALE, { month: "short", day: "numeric", year: "numeric" }),
        time: d.toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit" }),
    };
}

/**
 * Formats a Unix timestamp (seconds) to full locale string
 * @param timestamp - Unix timestamp in seconds (optional)
 */
export function formatTimestamp(timestamp?: number): string {
    return timestamp ? new Date(timestamp * 1000).toLocaleString(LOCALE) : EMPTY_VALUE;
}

/**
 * Formats a Unix timestamp (seconds) to date with year
 * @param timestamp - Unix timestamp in seconds (optional)
 */
export function formatDateFromTimestamp(timestamp?: number): string {
    if (!timestamp) return EMPTY_VALUE;
    return new Date(timestamp * 1000).toLocaleDateString(LOCALE, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Formats a Unix timestamp (seconds) to time string
 * @param timestamp - Unix timestamp in seconds (optional)
 */
export function formatTimeFromTimestamp(timestamp?: number): string {
    if (!timestamp) return EMPTY_VALUE;
    return new Date(timestamp * 1000).toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit" });
}

/**
 * Formats a Unix timestamp (seconds) to time with seconds (e.g., "02:30:45 PM")
 * @param timestamp - Unix timestamp in seconds
 */
export function formatTimeWithSeconds(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleTimeString(LOCALE, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/**
 * Formats a time string (HH:MM) to localized time
 * @param time - Time string in HH:MM format
 */
export function formatTimeRequired(time?: string): string {
    if (!time) return EMPTY_VALUE;
    const [hStr, mStr] = time.split(":");
    const hours = Number(hStr);
    const minutes = Number(mStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return time;
    const tmp = new Date();
    tmp.setHours(hours, minutes, 0, 0);
    return tmp.toLocaleTimeString(LOCALE, { hour: "numeric", minute: "2-digit" });
}

/**
 * Masks a phone number, showing only last 4 digits
 * @param phone - Full phone number
 */
export function maskPhone(phone: string): string {
    return phone.replace(/.(?=.{4})/g, "•");
}

/**
 * Removes leading +<country-code> from phone numbers for UI display/filtering.
 */
export function stripCountryCode(phone?: string | null): string {
    const value = typeof phone === "string" ? phone.trim() : "";
    if (!value) return "";
    if (!value.startsWith("+")) return value;
    return value.replace(/^\+\d{1,3}[\s-]*/, "").trim();
}

/**
 * Returns the value or a dash placeholder if empty
 * @param value - String value to display
 */
export function displayValue(value: string): string {
    return value ?? EMPTY_VALUE;
}

/**
 * Formats a window filter value (e.g. "17:00-21:00") to AM/PM range for display (e.g. "5:00 PM - 9:00 PM").
 * Use in filter dropdowns for the Window column. Returns the original value if it doesn't match "H:00-H:00" or "HH:00-HH:00".
 */
export function formatWindowFilterDisplay(value: string): string {
    if (!value || typeof value !== "string") return value ?? EMPTY_VALUE;
    // Range key for unnamed windows, e.g. "09:30-14:30"
    const formatted = formatWindowFilterValue(value);
    if (formatted !== value) return formatted;
    const match = value.trim().match(/^(\d{1,2}):00-(\d{1,2}):00$/);
    if (!match) return value;
    const start = parseInt(match[1], 10);
    const end = parseInt(match[2], 10);
    if (Number.isNaN(start) || Number.isNaN(end)) return value;
    return formatWindowRangeAMPM({ start, end });
}

/**
 * Converts ISO date string to YYYY-MM-DD format
 * Extracts date directly without timezone conversion
 * @param isoString - ISO date string
 */
export function formatDateFromISO(isoString: string): string {
    try {
        // Extract date portion directly to avoid timezone conversion issues
        return isoString.split("T")[0];
    } catch {
        return "";
    }
}

/**
 * Converts ISO timestamp string to HH:MM format
 * @param isoString - ISO timestamp string
 */
export function formatTimeRequiredFromISO(isoString: string): string {
    try {
        const date = new Date(isoString);
        const hours = date.getUTCHours().toString().padStart(2, "0");
        const minutes = date.getUTCMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    } catch {
        return "00:00";
    }
}
